import React from 'react';

import { Alert, Button, Form, Input, Space, Typography } from 'antd';
import { FormikProps } from 'formik';

import {
  IWebhookCredentialsUpsert,
  WEBHOOK_AUTH_TYPE,
  WebhookAuthType,
} from '../types';

export interface IWebhookFormValues extends IWebhookCredentialsUpsert {
  url: string;
}

interface IWebhookFormProps {
  isUpdating?: boolean;
  formik: FormikProps<IWebhookFormValues>;
}

const AUTH_TYPE_OPTIONS: { label: string; value: WebhookAuthType }[] = [
  { label: 'Public', value: WEBHOOK_AUTH_TYPE.PUBLIC },
  { label: 'Basic', value: WEBHOOK_AUTH_TYPE.BASIC },
  { label: 'API Key', value: WEBHOOK_AUTH_TYPE.API_KEY },
  { label: 'Header', value: WEBHOOK_AUTH_TYPE.HEADER },
  { label: 'Query Parameter', value: WEBHOOK_AUTH_TYPE.QUERY_PARAMETER },
];

export const WebhookForm = ({
  isUpdating = false,
  formik,
}: IWebhookFormProps) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    formik;

  return (
    <Form layout="vertical">
      <Form.Item
        label="URL:"
        validateStatus={touched.url && errors.url ? 'error' : ''}
        help={touched.url && errors.url ? errors.url : ''}
      >
        <Input
          name="url"
          value={values.url}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="https://"
          data-testid="webhook-url-input"
        />
      </Form.Item>

      <Typography.Text strong>AUTHORISATION TYPE:</Typography.Text>
      <Form.Item style={{ marginTop: 8 }}>
        <Space size={[8, 16]} wrap>
          {AUTH_TYPE_OPTIONS.map((option) => (
            <Button
              key={option.value}
              type={values.auth_type === option.value ? 'primary' : 'default'}
              onClick={() => setFieldValue('auth_type', option.value)}
              data-testid={`webhook-auth-${option.value}-button`}
            >
              {option.label}
            </Button>
          ))}
        </Space>
      </Form.Item>

      {values.auth_type === WEBHOOK_AUTH_TYPE.BASIC && (
        <>
          <Form.Item
            label="Username:"
            validateStatus={
              touched.basic_username && errors.basic_username ? 'error' : ''
            }
            help={
              touched.basic_username && errors.basic_username
                ? errors.basic_username
                : ''
            }
          >
            <Input.Password
              name="basic_username"
              value={values.basic_username ?? ''}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter username"
              data-testid="webhook-basic-username-input"
            />
          </Form.Item>
          <Form.Item
            label="Password:"
            validateStatus={
              touched.basic_password && errors.basic_password ? 'error' : ''
            }
            help={
              touched.basic_password && errors.basic_password
                ? errors.basic_password
                : ''
            }
          >
            <Input.Password
              name="basic_password"
              value={values.basic_password ?? ''}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter password"
              data-testid="webhook-basic-password-input"
            />
          </Form.Item>
        </>
      )}

      {(values.auth_type === WEBHOOK_AUTH_TYPE.HEADER ||
        values.auth_type === WEBHOOK_AUTH_TYPE.API_KEY) && (
        <>
          <Form.Item
            label="Header Name:"
            validateStatus={
              touched.header_name && errors.header_name ? 'error' : ''
            }
            help={
              touched.header_name && errors.header_name
                ? errors.header_name
                : ''
            }
          >
            <Input.Password
              name="header_name"
              value={values.header_name ?? ''}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g., Authorization"
              data-testid="webhook-header-name-input"
            />
          </Form.Item>
          <Form.Item
            label="Header Value:"
            validateStatus={
              touched.header_value && errors.header_value ? 'error' : ''
            }
            help={
              touched.header_value && errors.header_value
                ? errors.header_value
                : ''
            }
          >
            <Input.Password
              name="header_value"
              value={values.header_value ?? ''}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter header value"
              data-testid="webhook-header-value-input"
            />
          </Form.Item>
        </>
      )}

      {values.auth_type === WEBHOOK_AUTH_TYPE.QUERY_PARAMETER && (
        <>
          <Form.Item
            label="Query Parameter:"
            validateStatus={
              touched.query_parameter && errors.query_parameter ? 'error' : ''
            }
            help={
              touched.query_parameter && errors.query_parameter
                ? errors.query_parameter
                : ''
            }
          >
            <Input.Password
              name="query_parameter"
              value={values.query_parameter ?? ''}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g., api_key"
              data-testid="webhook-query-parameter-input"
            />
          </Form.Item>
          <Form.Item
            label="Query Value:"
            validateStatus={
              touched.query_value && errors.query_value ? 'error' : ''
            }
            help={
              touched.query_value && errors.query_value
                ? errors.query_value
                : ''
            }
          >
            <Input.Password
              name="query_value"
              value={values.query_value ?? ''}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter query value"
              data-testid="webhook-query-value-input"
            />
          </Form.Item>
        </>
      )}
      {/* Show alert if updating */}
      {isUpdating && (
        <Alert
          message="Credentials are hidden once saved. You can only update them."
          type="warning"
        />
      )}
    </Form>
  );
};
